import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { playSound } from '@/sound/audioSystem';
import { eventBus } from '@/events/eventBus';
import { autoPingChannelFor } from '@/utils/commsHelpers';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { activeDeclineFlagForCustomer } from '@/domain/noDepositDeclineFlow';
import type { Booking, OverrideRequest } from '@/domain/types';

/**
 * No-deposit approval flow.
 *
 * A booking's deposit at or below the studio minimum can't be taken directly by
 * a non-privileged persona. Instead they raise an OverrideRequest with a
 * mandatory reason; the booking is created `held` and the request lands in
 * Georgina's Approvals queue. Georgina/Owner approve (booking → `confirmed`,
 * depositWaived) or reject (booking stays `held` for follow-up). Privileged
 * personas who waive directly record a pre-approved request for the same audit
 * trail. Everything is logged via appendActivity and published on the eventBus,
 * mirroring depositFlow / sessionFlow.
 */

/**
 * The studio's minimum acceptable deposit without approval, in AUD. A deposit at
 * or below this floor requires a no-deposit override. Defaults to 0, so only a
 * genuinely zero deposit trips the control today; raise it to route nominal
 * underpayments through approval too (single source of truth for the gate).
 */
export const STUDIO_MINIMUM_DEPOSIT_AUD = 0;

/** True when a deposit of this amount needs a no-deposit override to proceed. */
export function requiresNoDepositApproval(amountAud: number): boolean {
  return amountAud <= STUDIO_MINIMUM_DEPOSIT_AUD;
}

function nowIso() {
  return useSimulationStore.getState().clockIso;
}

/** The live pending override request for a booking, if any (else undefined). */
export function pendingOverrideRequestForBooking(
  bookingId: string,
  requests: Record<string, OverrideRequest>,
): OverrideRequest | undefined {
  return Object.values(requests).find(
    (r) => r.bookingId === bookingId && r.status === 'pending',
  );
}

/** All pending override requests, oldest first (the order Georgina works them). */
export function listPendingOverrideRequests(
  requests: Record<string, OverrideRequest>,
): OverrideRequest[] {
  return Object.values(requests)
    .filter((r) => r.status === 'pending')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Roughly how long ago a request was raised, as a short human label
 * ("just now", "3m", "2h", "1d"). Prototype-only, minute-resolution.
 */
export function requestAgeLabel(createdAtIso: string, nowMs: number): string {
  const ms = Math.max(0, nowMs - new Date(createdAtIso).getTime());
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Raise a no-deposit override request against a held booking. The booking must
 * already exist (the booking form creates it `held`); the reason note is
 * mandatory — an empty/blank note is refused (no request created, no mutation),
 * so no UI path can queue an unexplained waiver.
 */
export function requestNoDepositOverride(input: {
  bookingId: string;
  requestedBy: string;
  note: string;
}): OverrideRequest | undefined {
  const note = input.note.trim();
  if (!note) return undefined;
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return undefined;
  // tv-9rt: a prior Ops decline closes the no-money route for this customer. The
  // agent cannot raise a second no-deposit override (no return trip through Ops,
  // diagrams 05/09) and must collect an actual deposit; the flag clears on payment.
  if (activeDeclineFlagForCustomer(booking.customerId)) return undefined;

  const at = nowIso();
  const request: OverrideRequest = {
    id: `ovr-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    bookingId: input.bookingId,
    requestedBy: input.requestedBy,
    note,
    status: 'pending',
    createdAt: at,
  };
  brain.upsertOverrideRequest(request);
  brain.appendActivity({
    id: `act-${request.id}`,
    type: 'override-requested',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `No-deposit approval requested by ${input.requestedBy} — "${note}".`,
    timestamp: at,
  });
  const target = autoPingChannelFor('booking-created', booking.studioId, brain.internalComms);
  if (target) {
    brain.addAutoPing(
      target,
      `⏳ No-deposit approval requested · ${booking.studioId.replace('-', ' ')} · awaiting Georgina.`,
      { customerRefId: booking.customerId, timestamp: at },
    );
  }
  playSound('confirmation-tap');
  eventBus.publish('override:requested', {
    requestId: request.id,
    bookingId: input.bookingId,
  });
  return request;
}

/**
 * Transition a held booking to confirmed with the deposit waived. Shared by the
 * approve path and the privileged direct-waive path so the confirm logic (and
 * the auto-sent waiver) lives in exactly one place.
 */
function confirmWaivedBooking(booking: Booking): void {
  const brain = useBrainStore.getState();
  brain.upsertBooking({ ...booking, status: 'confirmed', depositWaived: true });
  // Booking just reached `confirmed` — auto-send the pre-session waiver, same as
  // every other path into `confirmed`.
  onBookingConfirmed(booking.id);
}

/**
 * Approve or reject a pending override request. Approve → the booking is
 * confirmed with depositWaived; reject → the booking stays `held` for the
 * requester to follow up (convert to a normal deposit or cancel). Idempotent:
 * resolving an already-resolved request is a no-op.
 */
export function resolveNoDepositOverride(input: {
  requestId: string;
  approve: boolean;
  resolvedBy: string;
  resolutionNote?: string;
}): OverrideRequest | undefined {
  const brain = useBrainStore.getState();
  const request = brain.overrideRequests[input.requestId];
  if (!request || request.status !== 'pending') return undefined;

  const at = nowIso();
  const resolved: OverrideRequest = {
    ...request,
    status: input.approve ? 'approved' : 'rejected',
    resolvedBy: input.resolvedBy,
    resolvedAt: at,
    resolutionNote: input.resolutionNote?.trim() || undefined,
  };
  brain.upsertOverrideRequest(resolved);

  const booking = brain.bookings[request.bookingId];
  if (booking && input.approve) {
    confirmWaivedBooking(booking);
  }

  if (booking) {
    brain.appendActivity({
      id: `act-${request.id}-resolved`,
      type: 'override-resolved',
      customerId: booking.customerId,
      studioId: booking.studioId,
      artistId: booking.artistId,
      body: input.approve
        ? `No-deposit booking approved by ${input.resolvedBy} — confirmed with deposit waived.`
        : `No-deposit request rejected by ${input.resolvedBy}${resolved.resolutionNote ? ` — "${resolved.resolutionNote}"` : ''}. Booking still held.`,
      timestamp: at,
    });
    const target = autoPingChannelFor('booking-created', booking.studioId, brain.internalComms);
    if (target) {
      brain.addAutoPing(
        target,
        input.approve
          ? `✅ No-deposit booking approved · ${booking.studioId.replace('-', ' ')} · confirmed, paying on the day.`
          : `⛔ No-deposit request rejected · ${booking.studioId.replace('-', ' ')} · follow up with a deposit link.`,
        { customerRefId: booking.customerId, timestamp: at },
      );
    }
  }

  playSound(input.approve ? 'booking-confirmed' : 'confirmation-tap');
  eventBus.publish('override:resolved', {
    requestId: request.id,
    bookingId: request.bookingId,
    approved: input.approve,
  });
  return resolved;
}

/**
 * Privileged direct path: Georgina/Owner waive a deposit at booking time. The
 * booking (already created `held`) is confirmed with depositWaived, and a
 * pre-approved OverrideRequest is recorded so the no-deposit booking still shows
 * an audit trail in the Approvals history. Note is mandatory, same as a request.
 */
export function waiveDepositDirectly(input: {
  bookingId: string;
  by: string;
  note: string;
}): OverrideRequest | undefined {
  const note = input.note.trim();
  if (!note) return undefined;
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return undefined;

  const at = nowIso();
  const request: OverrideRequest = {
    id: `ovr-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    bookingId: input.bookingId,
    requestedBy: input.by,
    note,
    status: 'approved',
    createdAt: at,
    resolvedBy: input.by,
    resolvedAt: at,
  };
  brain.upsertOverrideRequest(request);
  confirmWaivedBooking(booking);
  brain.appendActivity({
    id: `act-${request.id}`,
    type: 'override-resolved',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${input.by} waived the deposit directly — "${note}". Confirmed, paying on the day.`,
    timestamp: at,
  });
  const target = autoPingChannelFor('booking-created', booking.studioId, brain.internalComms);
  if (target) {
    brain.addAutoPing(
      target,
      `✅ No-deposit booking · ${booking.studioId.replace('-', ' ')} · waived by ${input.by}, paying on the day.`,
      { customerRefId: booking.customerId, timestamp: at },
    );
  }
  playSound('booking-confirmed');
  eventBus.publish('override:resolved', {
    requestId: request.id,
    bookingId: input.bookingId,
    approved: true,
  });
  return request;
}
